import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Download, Globe, Zap, Shield, Mail, Phone, MapPin } from 'lucide-react';

const ShippingPage = () => {
  const digitalFeatures = [
    {
      icon: Download,
      title: 'Instant Access',
      description: 'Start learning in seconds, not days'
    },
    {
      icon: Globe,
      title: 'Study Anywhere',
      description: 'Access from any device, anywhere'
    },
    {
      icon: Zap,
      title: 'No Waiting',
      description: 'No shipping, no delays, just learning'
    },
    {
      icon: Shield,
      title: 'Secure',
      description: 'Your content is always safe'
    }
  ];

  const sections = [
    {
      title: '1. Digital-Only Learning',
      content: `PhysicsNotebook is 100% digital. No physical books, no shipping, no waiting.
      
      What you get:
      • Instant access to all study materials
      • Interactive problems and solutions
      • Progress tracking
      • Video explanations
      • Practice tests`
    },
    {
      title: '2. Getting Started is Easy',
      content: `1. Sign up and subscribe
      2. Get instant access
      3. Start learning immediately
      
      No shipping address needed. No waiting for delivery.`
    },
    {
      title: '3. Access Anytime, Anywhere',
      content: `Your content is always available:
      
      • 24/7 access on any device
      • Pick up where you left off
      • Works on phones, tablets, and computers`
    },
    {
      title: '4. Need Help?',
      content: `We're here for you:
      
      📧 Email: aditya@physicsnotebook.in
      📱 WhatsApp: +91 9309242445
      
      We usually respond within 24 hours.`
    }
  ];

  return (
    <>
      <Helmet>
        <title>Digital Delivery - PhysicsNotebook.in | Instant Access</title>
        <meta name="description" content="PhysicsNotebook.in provides instant digital access to all study materials. No shipping, no waiting - start learning physics right away!" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-gradient section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Instant <span className="gradient-text">Digital Access</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            No shipping, no waiting. Get instant access to all your physics study materials.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm text-gray-500 mt-4"
          >
            Last updated: August 2024
          </motion.p>
        </div>
      </section>

      {/* Digital Delivery Features */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Learning Without Limits</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Study physics anytime, anywhere, on any device. No shipping, no waiting, just learning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {digitalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-gray-50 rounded-xl p-6 card-hover"
              >
                <div className="bg-gradient-to-r from-blue-600 to-green-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
            <div className="flex items-center mb-2">
              <Download className="h-6 w-6 text-blue-600 mr-2" />
              <p className="text-blue-800 font-medium">100% Digital Platform</p>
            </div>
            <p className="text-blue-700 text-sm">
              PhysicsNotebook.in is a completely digital platform. We do not sell or ship any physical products. 
              All educational content is delivered instantly through our secure online platform.
            </p>
          </div>
        </div>
      </section>

      {/* Policy Details */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Access Instructions */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How to Access Your Content</h2>
            <p className="text-xl text-gray-600">
              Simple steps to start learning immediately after purchase.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Purchase & Payment</h3>
              <p className="text-gray-600">Complete your purchase using our secure payment system. Payment confirmation is instant.</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Account Creation</h3>
              <p className="text-gray-600">Receive login credentials via email and create your personalized learning account.</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Start Learning</h3>
              <p className="text-gray-600">Access your dashboard and begin your physics learning journey immediately.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Common questions about our digital content delivery.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Do you ship any physical products?</h3>
              <p className="text-gray-600">No, we are a 100% digital platform. All our educational content is delivered electronically through our online platform. No physical products are sold or shipped.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How quickly can I access my content after purchase?</h3>
              <p className="text-gray-600">You get instant access! Once your payment is confirmed, you'll receive login credentials via email and can start accessing your content within minutes.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I download content for offline use?</h3>
              <p className="text-gray-600">Yes, premium subscribers can download PDF notes and practice problems for offline study. However, interactive features require an internet connection.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What if I can't access my purchased content?</h3>
              <p className="text-gray-600">Contact our technical support team immediately at tech@physicsnotebook.in. We provide 24/7 support to ensure you can access your content without any issues.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Is there any shipping cost?</h3>
              <p className="text-gray-600">No shipping costs! Since we deliver everything digitally, there are no shipping fees, delivery charges, or additional costs beyond your subscription price.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-gradient-to-r from-blue-600 to-green-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Need Help Accessing Your Content?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Our technical support team is here to ensure you have seamless access to all your digital learning materials.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:tech@physicsnotebook.in" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors">
                Technical Support
              </a>
              <a href="mailto:support@physicsnotebook.in" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 rounded-lg font-semibold transition-colors">
                General Support
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ShippingPage;
